import { useEffect, useState, type CSSProperties } from 'react';

/**
 * The one orchestrated moment (Section 9): the arrival page opens on the
 * island from the water, and its pieces arrive in order. It plays once per
 * visit to the app, on the first thing the guest opens, and never again until
 * the app is closed. Everything else in the app answers a tap and is still.
 *
 * Reduced motion is respected twice over: index.css collapses animation
 * durations, and the block below drops the animation and shows the end state.
 */

const FLAG = 'dia.guest.intro.played';

function alreadyPlayed(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(FLAG) === '1';
  } catch {
    return false;
  }
}

/** True on the first render of a visit, false for every one after. */
export function useIntro(): boolean {
  const [intro] = useState(() => !alreadyPlayed());
  useEffect(() => {
    try {
      globalThis.sessionStorage?.setItem(FLAG, '1');
    } catch {
      // Private mode. The moment may play twice, which is harmless.
    }
  }, []);
  return intro;
}

const CSS = `
@keyframes dia-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
@keyframes dia-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes dia-settle { from { opacity: 0; transform: scale(1.07); } to { opacity: 1; transform: scale(1); } }
@keyframes dia-lift { from { transform: translateY(22px); } to { transform: none; } }
.dia-intro .dia-settle { animation: dia-settle 2600ms cubic-bezier(.16,.7,.2,1) both; }
.dia-intro .dia-rise { animation: dia-rise 800ms cubic-bezier(.2,.7,.2,1) both; animation-delay: var(--d, 0ms); }
.dia-intro .dia-fade { animation: dia-fade 900ms ease-out both; animation-delay: var(--d, 0ms); }
.dia-intro .dia-lift { animation: dia-lift 1800ms cubic-bezier(.16,.7,.2,1) both; }
@media (prefers-reduced-motion: reduce) {
  .dia-intro .dia-settle, .dia-intro .dia-rise, .dia-intro .dia-fade, .dia-intro .dia-lift {
    animation: none !important; opacity: 1 !important; transform: none !important;
  }
}
`;

export function MotionStyles() {
  return <style>{CSS}</style>;
}

/** The stagger: how long an element waits, in ms, after the moment begins. */
export function delay(ms: number): CSSProperties {
  return { '--d': `${ms}ms` } as CSSProperties;
}
